import { addHours, subHours } from 'date-fns'
import { Field } from 'formik'
import React from 'react'
import { Element, Heading, Icon } from 'react-bulma-components'
import {
  dateToDateInputValue,
  dateToTimeInputValue,
  formatDateWithTime,
  isDateInRange,
} from '../../../../utils/date'
import InputField from '../../../form/fields/input'
import TextareaField from '../../../form/fields/textarea'
import Quote from '../../../quote/quote'
import { isAuthorizedFor } from '../../../protected/protected'
import { EditIcon, WarningIcon } from '../../../icons'
import { WorkPeriodClockingTimeDetailsProps } from './types'
import DescriptionList from '../../../description-list/description-list'

const ManualClockingFieldHelp: React.FC<{
  // TODO: refactor props
  manualClockingDate?: Date | null
  manualClockingUpdated?: boolean
  disabled?: boolean
  onEditManualClockingDay: () => void
  isNotInRange?: boolean | null
}> = ({
  manualClockingDate,
  manualClockingUpdated,
  disabled,
  onEditManualClockingDay,
  isNotInRange,
}) => {
  return (
    <>
      {manualClockingDate && manualClockingUpdated && !disabled ? (
        <Element
          onClick={() => onEditManualClockingDay()}
          renderAs={manualClockingUpdated ? 'a' : 'span'}
          textColor="grey"
        >
          <Icon.Text>
            {manualClockingUpdated && (
              <Icon size="small" mt={1}>
                <EditIcon />
              </Icon>
            )}
            {formatDateWithTime(manualClockingDate)}
          </Icon.Text>
        </Element>
      ) : (
        manualClockingDate && formatDateWithTime(manualClockingDate)
      )}
      {isNotInRange === true && (
        <Icon
          pull="right"
          color="warning"
          style={{ height: '20px', width: '20px' }}
          data-tooltip="La date de pointage excède de plus de 4 heures la plage horaire de la période de travail"
        >
          <WarningIcon />
        </Icon>
      )}
    </>
  )
}

/**
 * Theoretical time: read-only display with edit pencil, or time input when editing.
 */
const TheoreticalTimeField: React.FC<{
  clockingTime: 'start' | 'end'
  displayValue: string
  disabled: boolean
}> = ({ clockingTime, displayValue, disabled }) => {
  const [isEditing, setIsEditing] = React.useState(false)

  if (isEditing) {
    return (
      <Field
        type="time"
        name={`workPeriod.${clockingTime}.date`}
        component={InputField}
        dataTest={`work-period-${clockingTime}-theoretical`}
        disabled={disabled}
      />
    )
  }

  return (
    <Element
      renderAs="span"
      onClick={() => !disabled && setIsEditing(true)}
      style={{ cursor: disabled ? undefined : 'pointer' }}
      data-test={`work-period-${clockingTime}-theoretical-display`}
    >
      <Icon.Text>
        {!disabled && (
          <Icon size="small" mt={1}>
            <EditIcon />
          </Icon>
        )}
        {displayValue}
      </Icon.Text>
    </Element>
  )
}

/**
 * Composant pour afficher et gérer les détails de pointage (début ou fin).
 * Affiche les informations théoriques (éditables au clic sur le crayon), pointées et permet la correction manuelle.
 */
const WorkPeriodClockingTimeDetails: React.FC<WorkPeriodClockingTimeDetailsProps> = ({
  workPeriod,
  clockingTime,
  formManualClockingValue,
  formTheoreticalValue,
  manualClockingDate,
  onEditManualClockingDay,
  displayManualClockingDay,
  manualClockingUpdated,
  disabled,
}) => {
  const clockingData = workPeriod[clockingTime]
  const comment =
    clockingData &&
    clockingData.manualClocking &&
    clockingData.comments[clockingData.comments.length - 1]

  const inputFieldDisabled =
    !workPeriod.__actions.canBeManualClocked ||
    !isAuthorizedFor(['employerMember'], {
      resource: 'missions',
      name: 'updateWorkPeriodManualClocking',
    })

  /** Theoretical hours can be edited when the work period is updatable (same permission as updateWorkPeriod). */
  const theoreticalFieldDisabled =
    !workPeriod.__actions.canBeUpdated ||
    !isAuthorizedFor(['employerMember'], {
      resource: 'missions',
      name: 'updateWorkPeriod',
    })

  const isManualClockingValueModified =
    formManualClockingValue &&
    formManualClockingValue !== dateToTimeInputValue(clockingData.manualClocking)

  const isInRange =
    manualClockingDate &&
    isDateInRange(
      manualClockingDate,
      subHours(new Date(workPeriod.start.date), 4),
      addHours(new Date(workPeriod.end.date), 4),
    )

  const theoreticalDisplayValue = formTheoreticalValue ?? dateToTimeInputValue(clockingData.date)

  return (
    <DescriptionList
      labelColumnWidth={'20%'}
      items={[
        {
          label: <Heading size={4}>{clockingTime === 'start' ? 'Début' : 'Fin'}</Heading>,
          value: ' ',
        },
        {
          label: 'Théorique',
          value: (
            <TheoreticalTimeField
              clockingTime={clockingTime}
              displayValue={theoreticalDisplayValue}
              disabled={theoreticalFieldDisabled}
            />
          ),
        },
        {
          label: 'Pointé à',
          value: (
            <span data-test={`work-period-${clockingTime}-clocking-display`}>
              {clockingData.clocking ? dateToTimeInputValue(clockingData.clocking.date) : '/'}
            </span>
          ),
        },
        {
          label: 'Corrigé',
          value: (
            <>
              {displayManualClockingDay && (
                <Field
                  type="date"
                  name={`workPeriod.${clockingTime}.manualClockingDay`}
                  component={InputField}
                  disabled={inputFieldDisabled}
                  min={dateToDateInputValue(subHours(new Date(workPeriod.start.date), 12))}
                  max={dateToDateInputValue(addHours(new Date(workPeriod.start.date), 12))}
                />
              )}
              <Field
                type="time"
                name={`workPeriod.${clockingTime}.manualClocking`}
                component={InputField}
                dataTest={`work-period-${clockingTime}-manual-clocking`}
                disabled={inputFieldDisabled}
                help={
                  <ManualClockingFieldHelp
                    manualClockingDate={manualClockingDate}
                    manualClockingUpdated={manualClockingUpdated}
                    disabled={disabled}
                    onEditManualClockingDay={onEditManualClockingDay}
                    isNotInRange={isInRange === false}
                  />
                }
                min={dateToDateInputValue(subHours(new Date(workPeriod.start.date), 12))}
                max={dateToDateInputValue(addHours(new Date(workPeriod.start.date), 12))}
              />
            </>
          ),
        },
        {
          label: 'Commentaire',
          value:
            // Show field when textarea field value is modified
            isManualClockingValueModified ? (
              <Field
                component={TextareaField}
                name={`workPeriod.${clockingTime}.comment`}
                data-test={`work-period-${clockingTime}-comment`}
              />
            ) : comment ? (
              <Quote message={comment.message} sender={comment.user} />
            ) : (
              '/'
            ),
        },
      ]}
    />
  )
}

export default WorkPeriodClockingTimeDetails
